import { Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useFavorites } from '../context/FavoritesContext';

/**
 * Heart toggle button. Use anywhere we display a listing.
 *
 * Props:
 *   listingId  – string (required)
 *   variant    – "card" (overlaid on image) | "inline" (default, fits inline buttons)
 *   className  – extra classes
 */
export default function FavoriteButton({ listingId, variant = 'inline', className = '' }) {
  const navigate = useNavigate();
  const { isFavorite, toggle } = useFavorites();
  const active = isFavorite(listingId);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const result = await toggle(listingId);
    if (result?.needsAuth) {
      toast.info('Sign in to save listings');
      navigate('/auth');
      return;
    }
    if (result?.error) {
      toast.error('Could not update favorites');
      return;
    }
    toast.success(result?.favorited ? 'Saved to your favorites' : 'Removed from favorites');
  };

  if (variant === 'card') {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-label={active ? 'Remove from favorites' : 'Save listing'}
        data-testid={`favorite-btn-${listingId}`}
        className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-background/70 backdrop-blur-sm border border-white/10 flex items-center justify-center transition-all hover:bg-background/90 hover:scale-110 ${
          active ? 'text-rose-400' : 'text-white/70 hover:text-white'
        } ${className}`}
      >
        <Heart className={`w-4 h-4 ${active ? 'fill-current' : ''}`} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={active ? 'Remove from favorites' : 'Save listing'}
      data-testid={`favorite-btn-${listingId}`}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border transition-colors ${
        active
          ? 'border-rose-400/50 bg-rose-400/10 text-rose-300 hover:bg-rose-400/20'
          : 'border-border/40 text-muted-foreground hover:text-foreground hover:border-border/60'
      } ${className}`}
    >
      <Heart className={`w-4 h-4 ${active ? 'fill-current' : ''}`} />
      <span className="text-sm font-medium">{active ? 'Saved' : 'Save'}</span>
    </button>
  );
}
